/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
import React, { useCallback, useRef, Fragment } from "react";
import PropTypes from "prop-types";
import Panel from "../GUI/Panel";
import BarWidget from "./BarChart";
import EventHandlersManager from "../../controllers/EventHandlersManager";
import "../../aframe_components/GrabRotate";

function BarWidgetAdvanced({
    width,
    height,
    depth,
    setZoom,
    setRotation,
    setLockControls,
    aframeClasses,
    data,
    deviceData,
    manipulationData,
    ...otherAttributes
}) {
    const root = useRef();
    const dragPanel = useRef();
    const focused = useRef(false);

    const zoomHandler = useCallback(
        (e) => {
            e.stopPropagation();
            const deadZone = 0.3;
            if (!focused.current) return;
            if (
                (deviceData.inVRMode &&
                    manipulationData.enabled.VR.includes("zoom")) ||
                (!deviceData.inVRMode &&
                    manipulationData.enabled.desktop.includes("zoom"))
            ) {
                const isController = e.type !== "wheel";
                let y = isController ? e.detail.y * 2 : e.deltaY;
                if (Math.abs(y) > 10) y = 10 * (y > 0 ? 1 : -1);
                if (Math.abs(y) > deadZone) {
                    setZoom((old) => {
                        const scale = old - y / 100;
                        if (scale < 1 || scale > 1.7) return old;
                        return scale;
                    });
                }
            }
        },
        [setZoom, manipulationData, deviceData.inVRMode]
    );

    if (!data) return null;

    const graphScale =
        (deviceData.inVRMode && manipulationData.enabled.VR.includes("zoom")) ||
        (!deviceData.inVRMode &&
            manipulationData.enabled.desktop.includes("zoom"))
            ? `${data.position.scale} ${data.position.scale} ${data.position.scale}`
            : 1;
    const graphRotation =
        (deviceData.inVRMode &&
            manipulationData.enabled.VR.includes("rotation")) ||
        (!deviceData.inVRMode &&
            manipulationData.enabled.desktop.includes("rotation"))
            ? `${-90 + data.position.rotation.x} ${45 + data.position.rotation.y} ${
                  data.position.rotation.z
              }`
            : "-90 45 0";

    return (
        <a-entity class="BarWidgetAdvanced" {...otherAttributes}>
            <EventHandlersManager
                events={{
                    wheel: (e) => {
                        zoomHandler(e);
                    },
                    thumbstickmoved: (e) => {
                        zoomHandler(e);
                    },
                    trackpadmoved: (e) => {
                        zoomHandler(e);
                    },
                    touchpadmoved: (e) => {
                        zoomHandler(e);
                    },
                    mouseenter: {
                        element: dragPanel,
                        isReactRef: true,
                        callback: (e) => {
                            focused.current = true;
                        }
                    },
                    mouseleave: {
                        element: dragPanel,
                        isReactRef: true,
                        callback: (e) => {
                            focused.current = false;
                        }
                    }
                }}
                enabled={deviceData.canModify}
            />
            <EventHandlersManager
                events={{
                    mouseenter: {
                        element: root,
                        isReactRef: true,
                        callback: (e) => {
                            focused.current = true;
                        }
                    },
                    mouseleave: {
                        element: root,
                        isReactRef: true,
                        callback: (e) => {
                            focused.current = false;
                        }
                    }
                }}
                enabled={deviceData.canModify}
            />
            {(!deviceData.inVRMode ||
                deviceData.areControllersConnected ||
                (deviceData.inVRMode &&
                    !deviceData.areControllersConnected &&
                    !deviceData.isMobile)) && (
                <>
                    <EventHandlersManager
                        events={{
                            mousedown: {
                                element: dragPanel,
                                isReactRef: true,
                                callback: (e) => {
                                    dragPanel.current.setAttribute("grab-rotate", {
                                        lockCamera:
                                            deviceData.inVRMode &&
                                            !deviceData.areControllersConnected &&
                                            !deviceData.isMobile,
                                        setRotation: setRotation,
                                        setLockControls: setLockControls
                                    });
                                }
                            },
                            mouseup: {
                                element: dragPanel,
                                isReactRef: true,
                                callback: (e) => {
                                    dragPanel.current.removeAttribute("grab-rotate");
                                }
                            }
                        }}
                        enabled={deviceData.canModify}
                    />
                    <EventHandlersManager
                        events={{
                            mousedown: {
                                element: root,
                                isReactRef: true,
                                callback: (e) => {
                                    root.current.setAttribute("grab-rotate", {
                                        lockCamera:
                                            deviceData.inVRMode &&
                                            !deviceData.areControllersConnected &&
                                            !deviceData.isMobile,
                                        setRotation: setRotation,
                                        setLockControls: setLockControls
                                    });
                                }
                            },
                            mouseup: {
                                element: root,
                                isReactRef: true,
                                callback: (e) => {
                                    root.current.removeAttribute("grab-rotate");
                                }
                            }
                        }}
                        enabled={deviceData.canModify}
                    />
                </>
            )}
            <a-entity scale={graphScale}>
                {((deviceData.inVRMode && manipulationData.enabled.VR.length) ||
                    (!deviceData.inVRMode &&
                        manipulationData.enabled.desktop.length)) && (
                    <a-plane
                        id="grab-panel"
                        class={aframeClasses}
                        width={width * 1.5}
                        height={height * 1.7}
                        position={`0 ${height * 0.3} ${
                            -height * Math.sin((45 * Math.PI) / 180)
                        }`}
                        ref={dragPanel}
                        visible={false}
                    />
                )}
                <a-entity rotation={graphRotation}>
                    <BarWidgetAux
                        data={data.currentData || data.data}
                        width={width}
                        height={height}
                        depth={depth}
                        clue={data.position.clue}
                        rootRef={root}
                        aframeClasses={aframeClasses}
                    />
                </a-entity>
            </a-entity>
        </a-entity>
    );
}

const BarWidgetAux = React.memo(
    ({ width, depth, height, data, clue, rootRef, aframeClasses }) => {
        return (
            <Panel
                resize
                width={width}
                height={depth}
                depth={height}
                enableReload
                aframeClasses={aframeClasses}
            >
                <a-entity rotation="90 0 0" ref={rootRef}>
                    <BarWidget clue={clue} data={data} aframeClasses={aframeClasses} box />
                </a-entity>
            </Panel>
        );
    }
);

// requiring dataPoints
BarWidgetAdvanced.propTypes = {
    data: PropTypes.object.isRequired,
    deviceData: PropTypes.object.isRequired,
    manipulationData: PropTypes.object.isRequired,
    setZoom: PropTypes.func.isRequired,
    setRotation: PropTypes.func.isRequired,
    setLockControls: PropTypes.func.isRequired
};

export default BarWidgetAdvanced;
